import { Version } from '../../../redux/store/api/api';
import { ContentArea } from '../types';
import { CALCULATION_CONTENT_CONTAINER_ID } from '../../../shared/constants';
import getValidatedContentArea from '../../marks/functions';
import {
  getStartNodeContainerId,
  getPrecedingCharacters,
  getSubsequentCharacters,
} from './convertFromRange';
import convertToRange from './convertToRange';
import {
  getTagfreeContent,
  getContentAreaFromTrustedSuggestion,
} from './findSuggestion';
import makeContent from './makeContent';

/**
 * Sets the value for the properties `precedingCharacters`, `subsequentCharacters`, and `startNodeContainerId` if they are still `null`.
 *
 * Steps for each `ContentArea` object:
 * - Create an HTML `Document` object for the migration without displaying it to the user.
 * - Find the Range within the HTML document representing the `ContentArea` object.
 * - Calculate `precedingCharacters`, `subsequentCharacters`, and `startNodeContainerId`.
 * - Validate the `ContentArea` object (if migrated).
 *
 * @param {ContentArea[]} contentAreas - Array of `ContentArea` objects to migrate (if needed).
 * @param {Version} version - The `Version` object the `ContentArea` objects belong to.
 * @returns {ContentArea[]} New array containing all `ContentArea` objects, either migrated or unchanged.
 */
function getMigratedContentAreas(
  contentAreas: ContentArea[],
  version: Version,
): ContentArea[] {
  return contentAreas.map((p) => {
    if (
      p?.precedingCharacters === null ||
      p?.subsequentCharacters === null ||
      p?.startNodeContainerId === null
    ) {
      const parser = new DOMParser();
      const htmlDoc = parser.parseFromString(
        `<div id='${CALCULATION_CONTENT_CONTAINER_ID}'>${
          version.htmlContent || ''
        }</div>`,
        'text/html',
      );

      const range = convertToRange(
        htmlDoc,
        CALCULATION_CONTENT_CONTAINER_ID,
        p,
      );

      if (range) {
        const startNodeContainerId = getStartNodeContainerId(
          range.startContainer.parentElement,
        );
        const precedingCharacters = getPrecedingCharacters(
          range.cloneRange(),
          CALCULATION_CONTENT_CONTAINER_ID,
          htmlDoc,
        );
        const subsequentCharacters = getSubsequentCharacters(
          range.cloneRange(),
          CALCULATION_CONTENT_CONTAINER_ID,
          htmlDoc,
        );

        return getValidatedContentArea(
          {
            ...p,
            invalid: false,
            changed: true,
            precedingCharacters,
            subsequentCharacters,
            startNodeContainerId,
          },
          p,
        );
      }
    }

    return p;
  });
}

/**
 * Checks if the `Range` object represented by the `ContentArea` object can be found.
 * If not, the `ContentArea` object is updated if possible using {@link getContentAreaFromTrustedSuggestion}.
 *
 * @param {ContentArea[]} contentAreas - Array of `ContentArea` objects to update (if needed and possible).
 * @param {Version} version - The `Version` object the `ContentArea` objects belong to.
 * @returns {ContentArea[]} New array containing all `ContentArea` objects, either updated or unchanged.
 */
function getAutoUpdatedContentAreas(
  contentAreas: ContentArea[],
  version: Version,
): ContentArea[] {
  const testContentData = makeContent({
    content: version?.htmlContent || '',
    contentAreas,
  });
  const tagFreeTestContentData = getTagfreeContent(testContentData.content);

  return contentAreas.map((area) => {
    // area is found in content and was expected to be found -> no changes needed
    if (
      !testContentData.elementsNotFound.includes(area.id || '') &&
      !area.invalid
    ) {
      return area;
    }

    // area is found in content but was not expected to be found -> correct invalid flag
    if (
      !testContentData.elementsNotFound.includes(area.id || '') &&
      area.invalid
    ) {
      return { ...area, changed: true, invalid: false };
    }

    // area is not found in content

    const recalculatedArea = getContentAreaFromTrustedSuggestion(
      testContentData.content,
      tagFreeTestContentData,
      area,
    );

    // recalcultion sucessfull -> return recalculated area
    if (recalculatedArea) {
      return recalculatedArea;
    }

    // recalculation failed -> correct invalid flag if needed
    if (!area.invalid) {
      return { ...area, invalid: true, changed: true };
    }

    // recalculation failed, invalid flag already correct -> no changes needed
    return area;
  });
}

/**
 * Applies the migration ({@link getMigratedContentAreas}) followed by the automatic update ({@link getAutoUpdatedContentAreas}) to all `ContentArea` objects.
 *
 * @param {ContentArea[][]} contentAreas - Array of arrays containing `ContentArea` objects belonging to the same `Version` object.
 * Within each inner array, all `ContentArea` objects must be allowed to be present in the `Version` object content simultaneously.
 * @param {Version} version - The `Version` object the `ContentArea` objects belong to.
 * @returns {ContentArea[][]} New array of arrays containing all `ContentArea` objects, some altered through migration/update, some unchanged.
 */
function getMigratedAndAutoUpdatedContentAreas(
  contentAreas: ContentArea[][],
  version: Version,
): ContentArea[][] {
  return contentAreas.map((areas) => {
    const migratedAreas = getMigratedContentAreas(areas, version);
    return getAutoUpdatedContentAreas(migratedAreas, version);
  });
}

export default getMigratedAndAutoUpdatedContentAreas;
